import { makeAutoObservable, runInAction } from 'mobx'
import { MultiValue } from 'react-select'
import { KitFormDataType, LoadersKits, ProductsForKitType } from './types'
import { KitsServices } from '~/shared/api/services/kits'
import {
  KitByIdType,
  KitsType,
  LocalizationKitType,
  VariationsKitType,
} from '~/shared/api/services/kits/types'
import { handleAxiosError } from '~/shared/lib'
import { SelectOption } from '~/shared/ui'
import { ProductAvailability } from '~/features/kits/lib/model/types'

interface KitsParams {
  limit?: number
  offset?: number
  q?: string
  vendorCode?: string
  sku?: string
  stores?: string[]
  archive?: boolean
}

export const defaultLocalizations = {
  name: '',
  description: '',
  languageCode: '',
  kitId: '',
  seoDescription: '',
  seoKeywords: '',
  seoTitle: '',
}

export const defaultProductsByStoreId = {
  id: '',
  name: '',
  productId: '',
  quantity: 1,
}

export const defaultKitFormData = {
  storeId: '',
  name: '',
  vendor_code: '',
  sku: '',
  slug: '',

  localizations: [defaultLocalizations],
  enabled: false,
  updateBalances: false,
  sortOrder: 0,
  categories: [],

  brandId: '',
  manufacturerId: '',
  manufacturerVendorCode: '',
  youtubeCode: '',
  productAvailabilities: [],
}

export class KitsStore {
  kits: KitsType[] = []

  kitById: KitByIdType = {
    id: '',
    name: '',
    vendorCode: '',
    description: '',
    store: {
      id: '',
      name: '',
      legalEntity: '',
      domain: '',
    },
    sku: '',
    slug: '',
    brandId: '',
    youtubeCode: '',
    sortOrder: '',
    manufacturerId: '',
    manufacturerVendorCode: '',
    categories: [],
    productAvailabilities: [defaultProductsByStoreId],
    variations: [],
    optionValues: [],
    activeValueImages: [],
    images: { id: 0, type: '', activeValues: [] },
    enabled: false,
    updateBalances: false,
    isArchived: false,
    localizations: [defaultLocalizations],
  }

  kitFormData: KitFormDataType = defaultKitFormData

  productsByStoreId: ProductAvailability[] = []

  selectedProducts: MultiValue<SelectOption> = []

  productQuantities: Array<{ productId: string; quantity: number }> = []

  searchFilters = {
    q: '',
    vendorCode: '',
    sku: '',
    stores: [] as string[],
    archive: false,
  }

  productsForKit: ProductsForKitType[] = []

  loaders: LoadersKits = {
    kits: false,
    loadMoreKits: false,
    createKit: false,
    variations: false,
    kitById: false,
    productsByStoreId: false,
    updateKit: false,
    addArchive: false,
  }

  requestSettings = {
    limit: 20,
    offset: 0,
    isAllLoaded: false,
  }

  constructor() {
    makeAutoObservable(this)
  }

  setVariationField = <K extends keyof VariationsKitType>(
    index: number,
    name: K,
    value: VariationsKitType[K],
  ) => {
    runInAction(() => {
      if (this.kitById.variations && this.kitById.variations[index]) {
        const variation = this.kitById.variations[index]
        variation[name] = value
      }
    })
  }

  activeLoader = (key: keyof LoadersKits) => {
    this.loaders = { ...this.loaders, [key]: true }
  }

  closeLoader = (key: keyof LoadersKits) => {
    this.loaders = { ...this.loaders, [key]: false }
  }

  setFormData = <K extends keyof KitFormDataType>(
    key: K,
    value: KitFormDataType[K],
  ) => {
    this.kitFormData = { ...this.kitFormData, [key]: value }
  }

  setFormGeneralData = (
    key: string,
    value: string | string[] | boolean | number | null,
  ) => {
    this.kitById = { ...this.kitById, [key]: value }
  }

  setFormLocalizationData = (index: number, key: string, value: string) => {
    const updatedLocalizations = [...this.kitById.localizations]

    if (updatedLocalizations[index]) {
      updatedLocalizations[index] = {
        ...updatedLocalizations[index],
        [key]: value,
      }

      this.kitById = {
        ...this.kitById,
        localizations: updatedLocalizations,
      }
    }
  }

  clearKitFormData = () => {
    this.kitFormData = defaultKitFormData
    this.productsForKit = []
    this.selectedProducts = []
    this.productQuantities = []
  }

  setFormLocalizationDataCreate = (
    index: number,
    key: string,
    value: string,
  ) => {
    const updatedLocalizations = [...this.kitFormData.localizations]

    if (updatedLocalizations[index]) {
      updatedLocalizations[index] = {
        ...updatedLocalizations[index],
        [key]: value,
      }

      this.kitFormData = {
        ...this.kitFormData,
        localizations: updatedLocalizations,
      }
    }
  }

  setDefaultLocalizations = (defaultValues: LocalizationKitType[]) => {
    this.kitFormData.localizations = defaultValues
  }

  getKits = async (
    action?: 'loadMoreKits',
    config?: { params: KitsParams },
  ) => {
    if (action === 'loadMoreKits' && this.requestSettings.isAllLoaded) return
    this.activeLoader(action || 'kits')

    try {
      const baseParams = {
        limit: this.requestSettings.limit,
        offset: action !== 'loadMoreKits' ? 0 : this.requestSettings.offset,
      }

      const params = config
        ? { ...baseParams, ...config.params }
        : { ...baseParams }

      const { data } = await KitsServices.getKits({ params })

      if (action === 'loadMoreKits') {
        runInAction(() => {
          this.kits = [...this.kits, ...data]
        })
      } else {
        runInAction(() => {
          this.kits = data
        })
      }

      this.requestSettings = {
        ...this.requestSettings,
        isAllLoaded: data.length < this.requestSettings.limit,
        offset: action
          ? this.requestSettings.offset + this.requestSettings.limit
          : 20,
      }
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader(action || 'kits')
    }
  }

  getKitById = async (id: string) => {
    this.activeLoader('kitById')
    try {
      const kitByIdData = await KitsServices.getKitById(id)

      runInAction(() => {
        this.kitById = kitByIdData.data
      })
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('kitById')
    }
  }

  getProductsByStoreId = async (storeId: string) => {
    this.activeLoader('productsByStoreId')
    try {
      const { data } = await KitsServices.getProductsByStoreId(storeId)

      runInAction(() => {
        this.productsByStoreId = data
      })
    } catch (error) {
      const errorMessage = handleAxiosError(error)
      throw new Error(errorMessage)
    } finally {
      this.closeLoader('productsByStoreId')
    }
  }

  setSelectedProducts = (value: MultiValue<SelectOption>) => {
    this.selectedProducts = value
  }

  setProductQuantities = (productId: string, quantity: number) => {
    const filteredQuantities = this.productQuantities.filter(
      (pq) => pq.productId !== productId,
    )

    this.productQuantities = [...filteredQuantities, { productId, quantity }]
  }

  setKitProducts = () => {
    const data = this.selectedProducts.map((select) => {
      const quantity = this.productQuantities.find(
        (pq) => pq.productId === select.value,
      )

      return {
        id: select.value,
        name: select.label,
        quantity: quantity?.quantity || 1,
      }
    })

    runInAction(() => {
      this.productsForKit = data

      this.kitFormData = {
        ...this.kitFormData,
        productAvailabilities: [
          ...this.kitFormData.productAvailabilities,
          ...data.map((item) => ({
            id: item.id,
            quantity: item.quantity,
          })),
        ],
      }
    })
  }

  setSearchFilters = <K extends keyof typeof this.searchFilters>(
    key: K,
    value: (typeof this.searchFilters)[K],
  ) => {
    runInAction(() => {
      this.searchFilters[key] = value
    })
  }
}
